import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

import bs from './locales/bs.json';
import hr from './locales/hr.json';
import mk from './locales/mk.json';
import sr from './locales/sr.json';

/**
 * The UI language follows the fair's `language` (ISO 639-1, from the
 * `language` table), so each country's deployment speaks its own language.
 * Adding a language means dropping in a locale file and listing it here, so no
 * string has to be extracted from a component later.
 */
export const DEFAULT_LANGUAGE = 'hr';

export const resources = {
  hr: { translation: hr },
  sr: { translation: sr },
  bs: { translation: bs },
  mk: { translation: mk },
} as const;

export type SupportedLanguage = keyof typeof resources;

export function isSupportedLanguage(value: string): value is SupportedLanguage {
  return Object.hasOwn(resources, value);
}

/**
 * Switches to the fair's language, or back to the default when the fair has
 * none or names one without a locale file — a fair must never render raw keys.
 */
export function applyFairLanguage(languageId: string | undefined): SupportedLanguage {
  const language =
    languageId !== undefined && isSupportedLanguage(languageId) ? languageId : DEFAULT_LANGUAGE;

  document.documentElement.lang = language;

  if (i18n.language !== language) {
    void i18n.changeLanguage(language);
  }

  return language;
}

void i18n.use(initReactI18next).init({
  resources,
  lng: DEFAULT_LANGUAGE,
  fallbackLng: DEFAULT_LANGUAGE,
  interpolation: { escapeValue: false },
});

export default i18n;
